<template>
	<div class="toast-container">
		<transition-group name="toast" tag="div" class="toast-stack">
			<div
				v-for="notification in notifications"
				:key="notification.id"
				class="toast-notification"
				:class="`toast-${notification.type}`"
				@click="remove(notification.id)"
			>
				<div class="toast-content">
					<div class="toast-icon">
						<v-icon :icon="getIcon(notification.type)" size="20" />
					</div>
					<div class="toast-message">{{ notification.message }}</div>
					<div class="toast-close">
						<v-icon
							icon="mdi-close"
							size="16"
							@click.stop="remove(notification.id)"
						/>
					</div>
				</div>

				<!-- Barra de progresso -->
				<div class="toast-progress" :class="`progress-${notification.type}`">
					<div
						class="toast-progress-bar"
						:style="{ animationDuration: `${notification.timeout}ms` }"
					></div>
				</div>

				<!-- Ações customizadas se houver -->
				<div v-if="notification.actions" class="toast-actions">
					<button
						v-for="(action, index) in notification.actions"
						:key="index"
						class="toast-action-btn"
						@click.stop="action.action"
					>
						{{ action.text }}
					</button>
				</div>
			</div>
		</transition-group>
	</div>
</template>

<script setup lang="ts">
import { useNotification } from "@/composables/useNotification";

// Composables
const { notifications, remove } = useNotification();

// Methods
const getColor = (type: string) => {
	const colors = {
		success: "success",
		error: "error",
		warning: "warning",
		info: "info",
	};
	return colors[type as keyof typeof colors] || "info";
};

const getIcon = (type: string) => {
	const icons = {
		success: "mdi-check-circle",
		error: "mdi-alert-circle",
		warning: "mdi-alert",
		info: "mdi-information",
	};
	return icons[type as keyof typeof icons] || "mdi-information";
};
</script>

<style scoped>
/* Container principal posicionado no canto inferior direito */
.toast-container {
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 9999;
	pointer-events: none;
	max-width: 400px;
}

.toast-stack {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* Toast individual */
.toast-notification {
	pointer-events: auto;
	background: rgb(var(--v-theme-surface));
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
	border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
	overflow: hidden;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	backdrop-filter: blur(10px);
	min-width: 320px;
	max-width: 400px;
}

.toast-notification:hover {
	transform: translateY(-2px);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

/* Conteúdo do toast */
.toast-content {
	display: flex;
	align-items: flex-start;
	padding: 16px 20px;
	gap: 12px;
}

.toast-icon {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	margin-top: 2px;
}

.toast-message {
	flex: 1;
	font-size: 14px;
	line-height: 1.5;
	color: rgb(var(--v-theme-on-surface));
	font-weight: 500;
}

.toast-close {
	flex-shrink: 0;
	cursor: pointer;
	opacity: 0.6;
	transition: opacity 0.2s;
	padding: 4px;
	margin: -4px -4px -4px 0;
}

.toast-close:hover {
	opacity: 1;
}

/* Barra de progresso */
.toast-progress {
	height: 3px;
	width: 100%;
	overflow: hidden;
	position: relative;
}

.toast-progress-bar {
	height: 100%;
	width: 100%;
	transform-origin: left;
	animation: progress-shrink linear forwards;
}

@keyframes progress-shrink {
	from {
		transform: scaleX(1);
	}
	to {
		transform: scaleX(0);
	}
}

/* Ações customizadas */
.toast-actions {
	padding: 0 20px 16px;
	display: flex;
	gap: 8px;
	justify-content: flex-end;
}

.toast-action-btn {
	background: none;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	border-radius: 6px;
	padding: 6px 12px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
}

.toast-action-btn:hover {
	background: rgba(var(--v-theme-surface), 0.8);
	border-color: rgba(var(--v-theme-on-surface), 0.2);
}

/* Tipos de toast */
.toast-success {
	border-left: 4px solid rgb(var(--v-theme-success));
}

.toast-success .toast-icon {
	background: rgba(var(--v-theme-success), 0.1);
	color: rgb(var(--v-theme-success));
}

.toast-success .toast-progress-bar {
	background: rgb(var(--v-theme-success));
}

.toast-error {
	border-left: 4px solid rgb(var(--v-theme-error));
}

.toast-error .toast-icon {
	background: rgba(var(--v-theme-error), 0.1);
	color: rgb(var(--v-theme-error));
}

.toast-error .toast-progress-bar {
	background: rgb(var(--v-theme-error));
}

.toast-warning {
	border-left: 4px solid rgb(var(--v-theme-warning));
}

.toast-warning .toast-icon {
	background: rgba(var(--v-theme-warning), 0.1);
	color: rgb(var(--v-theme-warning));
}

.toast-warning .toast-progress-bar {
	background: rgb(var(--v-theme-warning));
}

.toast-info {
	border-left: 4px solid rgb(var(--v-theme-info));
}

.toast-info .toast-icon {
	background: rgba(var(--v-theme-info), 0.1);
	color: rgb(var(--v-theme-info));
}

.toast-info .toast-progress-bar {
	background: rgb(var(--v-theme-info));
}

/* Animações de entrada e saída */
.toast-enter-active {
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.toast-leave-active {
	transition: all 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.toast-enter-from {
	transform: translateX(100%) scale(0.8);
	opacity: 0;
}

.toast-leave-to {
	transform: translateX(100%) scale(0.8);
	opacity: 0;
}

.toast-move {
	transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Responsivo */
@media (max-width: 480px) {
	.toast-container {
		left: 20px;
		right: 20px;
		bottom: 20px;
		max-width: none;
	}

	.toast-notification {
		min-width: auto;
		max-width: none;
	}

	.toast-content {
		padding: 14px 16px;
	}

	.toast-message {
		font-size: 13px;
	}
}
</style>
